import FIMenuForCustomerMeeting, { ICustomerLite } from '@core/classes/FIMenuForCustomerMeeting';
import { IDocumentOverlay } from '@core/classes/Paperwork/DocumentOverlay';
import ENUMS from '@core/classes/Enums';
import FIMenu from '@core/classes/FIMenu';
import FIMenuCustomer from '@core/classes/FIMenuCustomer';
import { IPaperworkDocument } from '@core/classes/Paperwork/PaperworkDocument';
import { IPaperworkPacket } from '@core/classes/Paperwork/PaperworkPacket';

/**
 * Traverses all of the documents in a packet and returns an array of signer IDs (excluding F&I mgr).
 * This makes sure to only return unique IDs across all documents of the packet
 * @param packet The packet to get the signer IDs from.
 * @returns An array of signer IDs.
 */
export function getSignerIdsFromPacket(packet: IPaperworkPacket): string[] {
	const signerIds: string[] = [];

	packet.documents.forEach(currDoc => {
		currDoc?.overlays?.forEach(o => {
			if (o.signatureMapping && o.signatureMapping.personType !== ENUMS.PERSON_TYPES.FIMANAGER && !signerIds.includes(o.signatureMapping.signerId)) {
				signerIds.push(o.signatureMapping.signerId);
			}
		});
	});
	return signerIds;
}

/**
 * Traverses all of the overlays in a document and returns an array of signer IDs (excluding F&I mgr).
 *
 * @param doc The document to get the signer IDs from.
 * @returns An array of signer IDs.
 */
export function getSignerIdsFromDocument(doc: IPaperworkDocument): string[] {
	const signerIds: string[] = [];
	const overlays = doc.overlays?.filter(
		o => o.overlayType === ENUMS.OVERLAY_TYPES.SIGNATURE || o.overlayType === ENUMS.OVERLAY_TYPES.INITIALS || o.overlayType === ENUMS.OVERLAY_TYPES.NOTARY_STAMP,
		// && (o.signature?.personType !== ENUMS.PERSON_TYPES.FIMANAGER && o.initials?.personType !== ENUMS.PERSON_TYPES.FIMANAGER)
	);

	if (overlays) {
		overlays.forEach(o => {
			if (o.signatureMapping && !signerIds.includes(o.signatureMapping.signerId)) {
				signerIds.push(o.signatureMapping.signerId);
			}
		});
	}
	return signerIds;
}

/**
 * Gets the FIMenuCustomer from FIMenu from a signer ID and an overlay.
 *
 * @param signerId The ID of the signer.
 * @param overlay The overlay to get the person signing from.
 * @param fimenu The FIMenu object.
 * @returns ICustomerLite object of the person signing.
 */
export function getPersonSigningFromOverlay(signerId: string, overlay: IDocumentOverlay, fimenu: FIMenuForCustomerMeeting | FIMenu): ICustomerLite {
	let personSigning = null;

	if (signerId == fimenu.customer.id) {
		personSigning = fimenu.customer;
	} else if (signerId == fimenu.coCustomer.id) {
		personSigning = fimenu.coCustomer;
	} else if (overlay.signatureMapping.personType === ENUMS.PERSON_TYPES.FIMANAGER && signerId == overlay.signatureMapping.signerId) {
		personSigning = {
			fullName:
				overlay.signature?.signerName?.split(',').reverse().join(' ').trim().toUpperCase() ||
				overlay.initials?.signerName?.split(',').reverse().join(' ').trim().toUpperCase() ||
				overlay.notaryStamp?.signerName?.split(',').reverse().join(' ').trim().toUpperCase() ||
				'N/A',
		};
	} else {
		personSigning = fimenu.customersInfo.customers.find((c: ICustomerLite) => c.id == overlay.signatureMapping.signerId);
	}
	return personSigning;
}

/**
 * Gets the FIMenuCustomer from FIMenu from a signer ID and a document.
 *
 * @param signerId The ID of the signer.
 * @param document The document to get the person signing from.
 * @param fimenu The FIMenu object.
 * @returns ICustomerLite object of the person signing.
 */
export function getPersonSigningFromDocument(signerId: string, doc: IPaperworkDocument, fimenu: FIMenuForCustomerMeeting | FIMenu): ICustomerLite {
	let personSigning = null;

	const signerOverlay = doc.overlays.find(o => o?.signatureMapping?.signerId == signerId);
	if (signerOverlay) {
		personSigning = getPersonSigningFromOverlay(signerId, signerOverlay, fimenu);
	}
	return personSigning;
}

/**
 * Gets the FIMenuCustomer object from FIMenu with the given ID.
 * @param id The ID of the person to get
 * @param fimenu The FI Menu object
 * @returns FIMenuCustomer object of the person with the given ID
 */
export function getPersonById(id: string, fimenu: FIMenuForCustomerMeeting | FIMenu): ICustomerLite | FIMenuCustomer {
	if (id == fimenu.customer.id) {
		return fimenu.customer;
	} else if (id == fimenu.coCustomer.id) {
		return fimenu.coCustomer;
	} else {
		return fimenu.customersInfo.customers.find((c: ICustomerLite) => c.id == id);
	}
}

/**
 * Checks if a document requires a signature by the given signer id by checking the doc overlays
 * @param overlays DocumentOverlay[]
 * @param signerId string
 * @returns boolean
 */
export function requiresSigner(overlays: IDocumentOverlay[], signerId: string): boolean {
	if (!overlays) return false;

	for (const overlay of overlays) {
		if (overlay.initials?.personType == ENUMS.PERSON_TYPES.FIMANAGER || overlay.signature?.personType == ENUMS.PERSON_TYPES.FIMANAGER) continue;

		if (overlay.signatureMapping?.signerId == signerId) return true;
	}
}

export function isDocWetSignOnly(doc: IPaperworkDocument): boolean {
	return Boolean(doc?.impactDoc || doc?.canOnlyWetSign);
}

/**
 * Checks if all signatures and initials in a document have been signed.
 * @param doc IPaperworkDocument
 * @returns boolean
 */
export function isDocSigned(doc: IPaperworkDocument): boolean {
	if (doc.isWetSigned || isDocWetSignOnly(doc)) return true;
	if (doc.overlays) {
		return doc.overlays
			.filter(o => o.signature || o.initials)
			.filter(o => o.initials?.personType !== ENUMS.PERSON_TYPES.FIMANAGER && o.signature?.personType !== ENUMS.PERSON_TYPES.FIMANAGER)
			.every(o => (o.signature && o.signature.isSigned == true) || (o.initials && o.initials.isSigned == true));
	} else {
		return true;
	}
}

/** Checks if the signer requires to sign or not, and returns true if he doesn't have to sign or has signed. */
export function isDocSignedBySignerOrDone(doc: IPaperworkDocument, signerId: string): boolean {
	// Skip over documents that have already been wet signed or are configured to be printed on the impact printer.
	if (doc?.isWetSigned || isDocWetSignOnly(doc)) return true;

	if (doc?.overlays) {
		const signerOverlays = [...doc.overlays.filter(overlay => overlay?.signatureMapping?.signerId == signerId || overlay?.signatureMapping?.signerId == signerId)];
		if (doc.filledDoc == null || doc.impactDoc) return true;

		for (const overlay of signerOverlays) {
			if (overlay.overlayType == ENUMS.OVERLAY_TYPES.NOTARY_STAMP) return false;
			if (overlay.overlayType == ENUMS.OVERLAY_TYPES.SIGNATURE && !overlay.signature?.isSigned) return false;
			if (overlay.overlayType == ENUMS.OVERLAY_TYPES.INITIALS && !overlay.initials?.isSigned) return false;
		}
	}

	return true;
}

export function resetPersonsCurrentSignatureIds(signerId: string, fimenu: FIMenuForCustomerMeeting | FIMenu) {
	const personSigning = getPersonById(signerId, fimenu);
	if (!personSigning) return;
	personSigning.currentInitialsId = null;
	personSigning.currentSignatureId = null;
}

/**
 * Translates a canvas signature down to a baseline if the signature is above it.
 *
 * @param canvas The HTML canvas with the drawn signature.
 * @param threshold A percentage of the canvas height that determines where the baseline is.
 * @returns The final canvas with adjusted signature.
 */
export function adjustSignatureToBaseline(canvas: HTMLCanvasElement, threshold: number = 0.78): HTMLCanvasElement {
	const { width, height } = canvas;

	const ctx = canvas.getContext('2d');
	const imageData = ctx.getImageData(0, 0, width, height);

	// Find the maximum Y coordinate of pixels in signature
	let yMax = 0;
	for (let y = 0; y < height; y++) {
		for (let x = 0; x < width; x++) {
			// ImageData.data array is a flat list of RGBA values, so we need to calculate the true index
			const index = (y * width + x) * 4;
			// Index represents location of red channel, we need to add 3 to get to alpha channel
			const opacity = imageData.data[index + 3];

			if (opacity > 0 && y > yMax) {
				yMax = y;
			}
		}
	}

	// Check if maximum Y coordinate is above the baseline
	const baseline = height * threshold;
	if (yMax < baseline) {
		// Create an empty canvas with the same dimensions as the provided one.
		const finalCanvas = document.createElement('canvas');
		finalCanvas.width = width;
		finalCanvas.height = height;

		// Draw the original canvas onto the final canvas, offsetting it vertically by baseline - yMax
		const finalCtx = finalCanvas.getContext('2d');
		finalCtx.drawImage(canvas, 0, baseline - yMax, width, height);

		return finalCanvas;
	}

	return canvas;
}

/**
 * Gets the number of valid points in a signature pad. Only counts points that are within the canvas.
 * @param pad SignaturePad
 * @param canvas HTMLCanvasElement
 * @returns number
 */
export function getValidPoints(pad: any, canvas: HTMLCanvasElement): number {
	const { width, height } = canvas;
	const validPoints = pad._data.map((d: any) => d.points.filter((point: any) => point.x > 0 && point.y > 0 && point.x < width && point.y < height));
	return validPoints.reduce((acc: number, curr: any) => acc + curr.length, 0);
}
